// SearchFoncierResiduel.js
import React, {Fragment, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { connect } from "react-redux";
import AutoCompleteResult from "./AutoCompleteResult";
import FloatingInputLabel from "./FloatingInputLabel";
import Spinner from "react-bootstrap/Spinner";
import PropTypes from "prop-types";
import "./FloatingInputLabel.scss";

const SearchFoncierResiduel = (props) => {
   const [comChoice, setComChoice] = useState(null);
   const [comChoiceLabel, setComChoiceLabel] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const [libelleChoice, setLibelleChoice] = useState([]);
   const [parcelleChoice, setParcelleChoice] = useState([]);
   const [query, setQuery] = useState("");
   const [prefixe, setPrefixe] = useState();
   const [libelle, setLibelle] = useState();
   const [results, setResults] = useState([]);
   const [surface_min, setSurfaceMin] = useState(0);
   const [surface_max, setSurfaceMax] = useState(100000);
   const sectionRef = useRef(null);

   // on mount, load saved commune if any
   useEffect(() => {
      const savedCode = localStorage.getItem('comChoice');
      const savedLabel = localStorage.getItem('comChoiceLabel');
      if (savedCode && savedLabel) {
         setComChoice(savedCode);
         setComChoiceLabel(savedLabel);
         getLibelleFoncierResiduelByCommune(savedCode);
         getCommGeom(savedCode);
      }
   }, []);
   // Initialize with com if exists
   useEffect(() => {
      const initResult = async () => {
         if (props.levelTerr.trim() === "com") {
            try {
               const codesTerr = props?.entityCodeTerr ? JSON.parse(props.entityCodeTerr) : null;
               const comCode = codesTerr ? codesTerr[0].code : " ";
               if (comCode) {
                  setComChoice(comCode);
                  setComChoiceLabel(props.userComLabel);
                  await getLibelleFoncierResiduelByCommune(comCode);
                  await getCommGeom(comCode);
               }
            } catch (error) {
               console.error("Error initializing SearchFoncierResiduel:", error);
            }
         }
      };
      initResult();
   }, [props.levelTerr, props.entityCodeTerr]);

   // Handle input changes
   const handleChange = useCallback((e) => {
      setLibelleChoice([]);
      setParcelleChoice([]);
      props.clearVectorLayer();
      setQuery(e?.target?.value);
   }, [props]);

   const handleSectionChange = useCallback(() => {
      const sectionValue = sectionRef.current.value;
      setParcelleChoice([]);
      setLibelle(sectionValue);
      props.clearVectorLayer();
   }, [props, comChoice]);

   const handleChangeSurfMin = useCallback((e) => {
      setSurfaceMin(e.target.value);
   }, []);

   const handleChangeSurfMax = useCallback((e) => {
      setSurfaceMax(e.target.value);
   }, []);

   const getSearch = () => {
      if (libelle) {
         getResiduelZoneUrbaine(libelle);
      }
   }

   // Watch for filter tool status changes
   useEffect(() => {
      if (props.filterToolStat) {
         setLibelleChoice([]);
         setParcelleChoice([]);
         props.cleanVectorLayer();
      }
   }, [props.filterToolStat, props]);

   // Fetch commune results based on query
   const getResults = async () => {
      const minCharacters = props.minCharacters || 0;
      if (query?.length < minCharacters) return;

      try {
         setIsLoading(true);
         const response = await axios.post("Api/geodata/commune_ref", { search: query });
         setResults(response.data.data);
      } catch (error) {
         console.error("Error fetching commune results:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Handle selection of a commune
   const selectResult = async (label, insee) => {
      try {
         setIsLoading(true);
         const response = await axios.get(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(label)}&type=municipality`);
         if (response?.data?.features.length) {
            const currentResult = response.data.features[0];
            setComChoice(currentResult.properties.id);
            setComChoiceLabel(currentResult.properties.label);
            localStorage.setItem('comChoice', currentResult.properties.id);
            localStorage.setItem('comChoiceLabel', currentResult.properties.label);
            props.updateCenter(currentResult.geometry.coordinates);
            await getLibelleFoncierResiduelByCommune(currentResult.properties.id);
            await getCommGeom(currentResult.properties.id);
         }
      } catch (error) {
         console.error("Error selecting commune:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch sections based on commune code
   const getLibelleFoncierResiduelByCommune = async (cityCode) => {
      setComChoice(cityCode);
      const params = {
         code_com: cityCode,
         search_type: props.searchType,
      };
      try {
         props.loading(true);
         const response = await axios.post("/Api/geodata/getLibelleFoncierResiduelByCommune", params);
         const libelles = response.data;
         libelles.unshift("Choisissez");
         setLibelleChoice(libelles);
      } catch (error) {
         console.error("Error fetching libelles :", error);
      } finally {
         props.loading(false);
      }
   };

   // Fetch parcelles based on commune and section
   const getResiduelZoneUrbaine = async (sectionCode) => {
      const formattedSection = sectionCode.length < 2 ? "0" + sectionCode : sectionCode;
      setLibelle(formattedSection);
      setPrefixe("000");

      const params = {
         code_com: comChoice,
         prefixe: "000",
         libelle: formattedSection,
         surface_min: surface_min,
         surface_max: surface_max,
         search_type: props.searchType,
      };
      const url = "/Api/geodata/getResiduelZoneUrbaine";


      if (!sectionCode.includes("ecti")) {
         try {
            const response = await axios.post(url, params);
            const zones = response.data;
            props.setPropType(props.searchType);
            props.setPropGeomJson(zones);
            props.setPropParcelles(zones);
            props.displayPropList(true);
            //parcelles.unshift({ getnumero: null, numero: "Parcelles" });
            setParcelleChoice(zones);
         } catch (error) {
            console.error("Error fetching parcelles:", error);
         }
      }
   };

   // Fetch commune geometry
   const getCommGeom = async (codeCom) => {
      const params = { code_com: codeCom };
      try {
         setIsLoading(true);
         const response = await axios.post("/Api/geodata/geom/commune", params);
         if (response.data[0]) {
            props.updateVectorLayer(JSON.parse(response.data[0].json_build_object), "commune");
            props.setCurrentCom(codeCom);
         }
      } catch (error) {
         console.error("Error fetching commune geometry:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Reset commune selection
   const handleResetCom = useCallback(() => {
      localStorage.removeItem('comChoice');
      localStorage.removeItem('comChoiceLabel');
      setComChoice(null);
      setComChoiceLabel("");
      setLibelleChoice([]);
      setParcelleChoice([]);
      props.setCurrentCom(null);
   }, [props]);

   // Render options for sections
   const renderLibelleOptions = (item, key) => (
      <option key={key} value={item}>
         {item}
      </option>
   );

   return (
      <div className="d-flex flex-column">
         <div className="my-2">
            {!comChoice ? (
               <Fragment>
               <div className="form-group position-relative mb-3 mb-md-0 w-100">
                 <FloatingInputLabel
                   label="Commune INSEE"
                   id="comSearchInput"
                   className="input-proprioC"
                   onChange={handleChange}
                   onKeyUp={getResults}
                   value={query}
                   name="comSearchInput"
                 />
                  {isLoading && (
                   <Spinner
                     animation="border"
                     size="sm"
                     className="top-50"
                   />
                 )}
               </div>
              
               <AutoCompleteResult results={results} selectResult={selectResult} />
             </Fragment>


            ) : comChoiceLabel ? (
               <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     value={`${comChoiceLabel} (${comChoice})`}
                     aria-label="Commune"
                     className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                     readOnly
                  />
                  <button
                     className="btn btn-link border-0 ms-2"
                     type="button"
                     onClick={handleResetCom}
                     aria-label="Clear"
                  >
                     <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
                  </button>
               </div>
            ) : (
               <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
                  <input
                     type="text"
                     value={comChoice}
                     aria-label="Propriétaire"
                     className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1"
                     readOnly
                  />
                  <button
                     className="btn btn-link border-0 ms-2"
                     type="button"
                     onClick={handleResetCom}
                     aria-label="Clear"
                  >
                     <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
                  </button>
               </div>
            )}
         </div>

         {libelleChoice.length <= 0 ? (
            <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
               <div className="did-floating-label-content form-group mb-3 mb-md-0 w-100 section-input-container">
                  <input
                     type="text"
                     aria-label="Libellé"
                     className="did-floating-input-m form-control filter-tools-input w-100"
                     placeholder=""
                     disabled
                     style={{ opacity: "0.3" }}
                  />
                  <label className="did-floating-label-m">Libellé</label>
               </div>
            </div>
         ) : (
            <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
               <div className="did-floating-label-content form-group mb-3 mb-md-0 w-100 section-select-container">
                  <select
                     id="sectionControlSelect"
                     className="did-floating-input-m form-control filter-tools-input "
                     ref={sectionRef}
                     value={libelle}
                     onChange={handleSectionChange}
                  >
                     {libelleChoice.map(renderLibelleOptions)}
                  </select>
                  <label className="did-floating-label-m">Libellé</label>
               </div>
            </div>
         )}

         <div className="form-group my-2 mb-2 d-flex flex-column flex-md-row flex-grow-1">
            <div className="did-floating-label-content mb-md-0 mr-2 w-50">
               <input
                  type="number"
                  aria-label="Surface min."
                  id="surf_min"
                  className="did-floating-input-m form-control filter-tools-input text-center"
                  placeholder=""
                  onChange={handleChangeSurfMin}
                  autoComplete="off"
                  
               />
               <label className="did-floating-label-m">Surface min.</label>
            </div>
            <div className="did-floating-label-content mb-md-0 ml-2 w-50">
               <input
                  type="number"
                  aria-label="Surface max."
                  id="surf_max"
                  className="did-floating-input-m form-control filter-tools-input text-center"
                  placeholder=""
                  onChange={handleChangeSurfMax}
                  autoComplete="off"
               />
               <label className="did-floating-label-m">Surface max.</label>
            </div>
         </div>

         <div className="my-2 d-flex flex-column flex-md-row flex-grow-1">
         <button
              type="button"
              className="btn btn-primary w-100 text-white fw-bold text-uppercase"
              style={{height: 35, fontWeight: 700}}
              onClick={getSearch}
            >
              VALIDER
            </button>
         </div>
      </div>


   );
};

// Prop type checking
SearchFoncierResiduel.propTypes = {
   levelTerr: PropTypes.string.isRequired,
   entityCodeTerr: PropTypes.string,
   com: PropTypes.string,
   loading: PropTypes.func.isRequired,
   updateCenter: PropTypes.func.isRequired,
   updateVectorLayer: PropTypes.func.isRequired,
   togglePanelTab: PropTypes.func.isRequired,
   cleanVectorLayer: PropTypes.func.isRequired,
   setCurrentCom: PropTypes.func.isRequired,
   setCurrentSection: PropTypes.func.isRequired,
   searchType: PropTypes.string,
   findParcelleObject: PropTypes.func.isRequired,
   filterToolStat: PropTypes.bool,
   clearVectorLayer: PropTypes.func.isRequired,
};

// Default props
SearchFoncierResiduel.defaultProps = {
   entityCodeTerr: null,
   com: "",
   searchType: "",
   filterToolStat: false,
   comChoiceLabel: "",
};

const mapStateToProps = (state) => ({
   levelTerr: state.MeReducer?.entity?.level_terr,
   userCom: state.MapReducer.currentCom,
   userComLabel: state.MapReducer.currentComLabel,
   filterToolStat: state.SearchReducer.filterToolStat,
   entityCodeTerr: state.MeReducer.entity.codes_terr,
});

const mapDispatchToProps = (dispatch) => ({
   updateCenter: (e, z = 17) =>
      dispatch({
         type: "UPDATE_CENTER_MAP",
         data: { centerMap: e, zoom: z },
      }),
   findParcelleObject: (e) =>
      dispatch({
         type: "FIND_PARCELLE_ON_LAYER",
         data: { parcelleCode: e },
      }),
   updateVectorLayer: (geoJsonData, typeTerr) =>
      dispatch({
         type: "UPDATE_VECTOR_LAYER",
         data: { geoJsonData, typeTerr },
      }),
   togglePanelTab: (currentTab) =>
      dispatch({
         type: "TOGGLE_PANEL_TAB",
         data: { currentTab },
      }),
   cleanVectorLayer: (e) =>
      dispatch({
         type: "CLEAR_VECTOR_LAYER",
         data: e,
      }),
   setCurrentCom: (e) =>
      dispatch({
         type: "SET_CURRENT_COM",
         data: { currentCom: e },
      }),
   setCurrentSection: (e) =>
      dispatch({
         type: "SHOW_CURRENT_SECTION",
         data: e,
      }),
   loading: (isLoading) =>
      dispatch({
         type: "SET_LOADING",
         data: { isLoading },
      }),
   displayPropList: (display) =>
      dispatch({
         type: "DISPLAY_PROP_LIST_PARCELLES",
         data: { displayPropList: display },
      }),
   setPropParcelles: (parcelles) =>
      dispatch({
         type: "SET_PROP_PARCELLES",
         data: { propParcelles: parcelles },
      }),
   setPropGeomJson: (zones) =>
      dispatch({
         type: "SET_SEARCH_GEOMJSON",
         data: { propGeomJson: zones },
      }),
   setPropType: (propType) =>
      dispatch({ type: "SET_PROP_TYPE", data: { propType } }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchFoncierResiduel);
